import { FC, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import { CheckCircle as CheckCircleIcon } from "@material-ui/icons";
import { usePostHog } from "posthog-js/react";

import { Button, ErrorMessage, Modal } from "components";
import { formatCurrencyAmount } from "lib/format";
import { parseNumber } from "lib/parse";
import { DEFAULT_ERROR_MESSAGE } from "services/constants";
import { updateMinimumPrice } from "services/listings";
import {
  Dictionary,
  processErrors,
  translateFieldErrors,
} from "services/utils";
import theme from "theme";
import { circleIconSize } from "theme/constants";

import EditMinimumPriceForm, { FormValues } from "./EditMinimumPriceForm";

interface EditMinimumPriceModalProps {
  open: boolean;
  onClose: () => void;
  listingId: number;
  minimumPrice: number | null;
  buyNowPrice: number | null;
}

const titleByStep = [
  "Edit Minimum Bid",
  "Confirm Minimum Bid",
  "Minimum Bid Set",
];

const EditMinimumPriceModal: FC<EditMinimumPriceModalProps> = ({
  open,
  onClose,
  listingId,
  minimumPrice,
  buyNowPrice,
}) => {
  const posthog = usePostHog();
  const [step, setStep] = useState(0);
  const [values, setValues] = useState({
    minimumPrice,
  });
  const [errors, setErrors] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<
    Dictionary<keyof FormValues, string>
  >({});
  const [isLoading, setIsLoading] = useState(false);

  const handleEditMinimumPrice = async (value: number | null) => {
    setErrors([]);
    setFieldErrors({});
    try {
      setIsLoading(true);
      const res = await updateMinimumPrice(listingId, value, posthog);
      setValues({ minimumPrice: res.minimum_price });
      setStep(2);
    } catch (e) {
      if (e instanceof Error) {
        const { formErrors, fieldErrors } = processErrors(e.message, [
          "buy_now_price",
        ]);
        setErrors(formErrors ?? []);
        setFieldErrors(
          translateFieldErrors<FormValues>(fieldErrors, {
            buy_now_price: "minimumPrice",
          }),
        );
      } else {
        setErrors([DEFAULT_ERROR_MESSAGE]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetMinimumPrice = async () => {
    try {
      setIsLoading(true);
      await handleEditMinimumPrice(null);
      setStep(2);
    } catch (e) {
      setErrors([DEFAULT_ERROR_MESSAGE]);
    } finally {
      setIsLoading(false);
    }
  };

  const makeOnBackHandler = () => {
    if (1 === step) {
      return () => {
        setErrors([]);
        setStep(0);
      };
    } else {
      return undefined;
    }
  };

  return (
    <Modal
      title={titleByStep[step]}
      open={open}
      onClose={onClose}
      onBack={makeOnBackHandler()}
    >
      {step === 0 && (
        <>
          <Typography variant="body2">
            This will automatically reject all offers below this amount and any
            future offers below this amount will not be notified to you.
            Rejected offers cannot be countered.
          </Typography>
          <EditMinimumPriceForm
            onSave={async values => {
              setValues({
                minimumPrice: parseNumber(values.minimumPrice),
              });
              setStep(1);
            }}
            initialValues={{ minimumPrice: String(values.minimumPrice) }}
            fieldErrors={fieldErrors}
            buyNowPrice={buyNowPrice}
            isLoading={isLoading}
          />
          <Button
            customColor="red"
            fullWidth
            size="large"
            variant="text"
            onClick={handleResetMinimumPrice}
            disabled={isLoading}
            isLoading={isLoading}
          >
            Reset to Default
          </Button>
        </>
      )}
      {step === 1 && (
        <>
          <Typography component="div" gutterBottom>
            <Box fontSize={13} textAlign="center">
              Your listing will be immediately updated and open offers below
              this amount will be immediately rejected.
            </Box>
          </Typography>
          {errors.length > 0 && (
            <ErrorMessage
              title="Oops!"
              description={errors[0] || DEFAULT_ERROR_MESSAGE}
            />
          )}
          <Box m={2} textAlign="center">
            <Box fontSize={18} m={1}>
              Minimum Bid
            </Box>
            <Box
              fontSize={18}
              fontWeight={theme.typography.fontWeightMedium}
              m={1}
            >
              {formatCurrencyAmount("USD", values.minimumPrice)}
            </Box>
          </Box>
          <Box m={2} mt={4}>
            <Button
              id="btn-confirm-modal"
              customColor="green"
              size="large"
              variant="contained"
              fullWidth
              onClick={() => {
                handleEditMinimumPrice(values.minimumPrice);
              }}
              disabled={isLoading}
              isLoading={isLoading}
            >
              Confirm Edit
            </Button>
          </Box>
        </>
      )}
      {step === 2 && (
        <Typography component="div">
          <Box textAlign="center" m={2}>
            <CheckCircleIcon
              style={{
                fontSize: circleIconSize.big,
                color: theme.custom.palette.green.main,
              }}
            />
          </Box>
          <Box fontSize={15} textAlign="center" m={2}>
            Offers below {formatCurrencyAmount("USD", values.minimumPrice)} will
            be automatically rejected.
          </Box>
        </Typography>
      )}
    </Modal>
  );
};

export default EditMinimumPriceModal;
